import * as React from 'react';

import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';

import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';

import { useTheme } from '@mui/material/styles';

import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';

import type { ErrorObject } from 'ajv';

import {HTML_TAGS} from './I18n';
import {T, useTolgee} from "@tolgee/react";

// Define a type for the component props
interface HelpDialogProps {

	open: boolean,
	setOpen: React.Dispatch<React.SetStateAction<boolean>>,
	errors: ErrorObject[]
}

type ErrorRow = {
	label: string | undefined,
	error: string | undefined,
	description: string | undefined
}

// Use the 'FC' (Functional Component) generic from React, with our props type
const ValidationDialog: React.FC<HelpDialogProps> = ({ open, setOpen, errors }: HelpDialogProps) => {

	const {t} = useTolgee();

	const handleClose = () => {
		setOpen(false);
	};

	const descriptionElementRef = React.useRef<HTMLElement>(null);
	React.useEffect(() => {
		if (open) {
			const { current: descriptionElement } = descriptionElementRef;
			if (descriptionElement !== null) {
				descriptionElement.focus();
			}
		}
	}, [open]);

	const rows: ErrorRow[] = [];

	errors.forEach(errorObject => {
		let field = errorObject.instancePath.replaceAll('/', '.');
		if (field.startsWith('.')) {
			field = field.substring(1);
		}

		if (errorObject.params["missingProperty"] !== undefined) {
			// a property is missing
			if (field.length > 0) {
				field += '.';
			}
			field += errorObject.params["missingProperty"];

			rows.push({
				label: t(field + '.label'),
				error: t('page.mandatory_field_missing'),
				description: t(field + '.description')
			});
		} else {
			rows.push({
				label: t(field + '.label'),
				error: t(errorObject.message ?? 'page.input_error'),
				description: t(field + '.description')
			});
		}
	});

	const theme = useTheme();

	return (
		<React.Fragment>
			<Dialog
				open={open}
				onClose={handleClose}
				scroll={'paper'}
				aria-labelledby="validation-dialog-title"
				aria-describedby="validation-dialog-description"
			>
				<DialogTitle id="validation-dialog-title">
					<T
						keyName="page.dialog.validation.title"
						params={{
							icon: (rows.length ===0 ?
								<CheckCircleOutlineIcon aria-label={t("aria.success_icon")} style={{ color: theme.palette.success.main }}/> :
								<ErrorOutlineIcon aria-label={t("aria.error_icon")} style={{ color: theme.palette.error.main }} />),
							...HTML_TAGS}}
					/>
				</DialogTitle>
				<DialogContent dividers={true}>
					<DialogContentText
						id="validation-dialog-description"
						ref={descriptionElementRef}
						tabIndex={-1}
					>
						{
							rows.length === 0 ?
								<T
									keyName="page.dialog.validation.valid"
									params={{...HTML_TAGS}}
								/>
								:
								<React.Fragment>
									<T
										keyName="page.dialog.validation.invalid"
										params={{...HTML_TAGS}}
									/>
									<TableContainer component={Paper}>
										<Table aria-label={t("aria.dialog.validation.description_table")}>
											<TableHead>
												<TableRow>
													<TableCell>{t("page.dialog.validation.colum_field")}</TableCell>
													<TableCell>{t("page.dialog.validation.colum_error")}</TableCell>
													<TableCell>{t("page.dialog.validation.colum_description")}</TableCell>
												</TableRow>
											</TableHead>
											<TableBody>
												{
													rows.map((row) =>
													(<TableRow>
														<TableCell>{row.label}</TableCell>
														<TableCell>{row.error}</TableCell>
														<TableCell>{row.description}</TableCell>
													</TableRow>)
													)
												}
											</TableBody>
										</Table>
									</TableContainer>
								</React.Fragment>
						}


					</DialogContentText>
				</DialogContent>
				<DialogActions>
					<Button onClick={handleClose}>OK</Button>
				</DialogActions>
			</Dialog>
		</React.Fragment>
	);
}
export default ValidationDialog;